import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Link,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  VStack,
  useToast,
} from "@chakra-ui/react";
import emailjs from "emailjs-com";
import Section from "./Section";
import { contact, emailjsConfig } from "../data/content";

const icons = {
  phone: "M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.4.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.4 0 .7-.2 1l-2.3 2.2z",
  mail: "M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z",
  pin: "M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5c-1.4 0-2.5-1.1-2.5-2.5S10.6 6.5 12 6.5 14.5 7.6 14.5 9 13.4 11.5 12 11.5z",
  whatsapp:
    "M17.5 14.4c-.3-.1-1.7-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.7 1-.9 1.2-.2.2-.3.2-.6.1-1.7-.9-2.9-1.6-4-3.5-.3-.5.3-.5.9-1.6.1-.2 0-.4 0-.5 0-.1-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5 1.9.8 2.6.9 3.5.7.6-.1 1.7-.7 1.9-1.4.2-.7.2-1.2.2-1.4-.1-.1-.3-.2-.6-.3zM12 2a10 10 0 00-8.6 15L2 22l5.2-1.4A10 10 0 1012 2zm0 18.2c-1.5 0-3-.4-4.3-1.2l-.3-.2-3.1.8.8-3-.2-.3A8.2 8.2 0 1120.2 12 8.2 8.2 0 0112 20.2z",
};

function ContactMethod({ icon, label, value, href, external }) {
  const content = (
    <Stack direction="row" spacing={4} align="center">
      <Box
        w="42px"
        h="42px"
        borderRadius="lg"
        bg="brand.50"
        border="1px solid"
        borderColor="brand.200"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
      >
        <Box as="svg" viewBox="0 0 24 24" w="20px" h="20px" fill="currentColor" color="brand.600">
          <path d={icon} />
        </Box>
      </Box>
      <Box minW={0}>
        <Text fontSize="xs" color="ink.500" textTransform="uppercase" letterSpacing="0.08em" fontWeight={600}>
          {label}
        </Text>
        <Text fontWeight={600} color="ink.800" wordBreak="break-word">
          {value}
        </Text>
      </Box>
    </Stack>
  );

  if (!href) {
    return (
      <Box p={4} borderRadius="lg" border="1px solid" borderColor="ink.100" bg="white">
        {content}
      </Box>
    );
  }

  return (
    <Link
      href={href}
      isExternal={external}
      p={4}
      borderRadius="lg"
      border="1px solid"
      borderColor="ink.100"
      bg="white"
      display="block"
      transition="border-color 0.2s, transform 0.2s"
      _hover={{ textDecoration: "none", borderColor: "brand.400", transform: "translateY(-2px)" }}
    >
      {content}
    </Link>
  );
}

export default function Contact() {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [isSending, setIsSending] = useState(false);
  const toast = useToast();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSending(true);
    try {
      await emailjs.send(
        emailjsConfig.serviceId,
        emailjsConfig.templateId,
        formData,
        emailjsConfig.publicKey
      );
      toast({
        title: "Message sent",
        description: "Thank you for reaching out. We'll get back to you shortly.",
        status: "success",
        duration: 6000,
        isClosable: true,
        position: "top",
      });
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      toast({
        title: "Message not sent",
        description: `Something went wrong. Please try again, or email us directly at ${contact.email}.`,
        status: "error",
        duration: 9000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Section
      id="contact"
      bg="ink.50"
      title="Contact Us"
      subtitle="Tell us what your laboratory needs and we'll respond with a tailored quote."
    >
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 10, lg: 14 }} alignItems="start">
        <VStack align="stretch" spacing={4}>
          <Heading as="h3" fontSize="lg" mb={1}>
            Get in touch directly
          </Heading>
          <ContactMethod icon={icons.phone} label="Phone" value={contact.phoneDisplay} href={contact.phoneHref} />
          <ContactMethod
            icon={icons.whatsapp}
            label="WhatsApp"
            value={contact.phoneDisplay}
            href={contact.whatsappHref}
            external
          />
          <ContactMethod icon={icons.mail} label="Email" value={contact.email} href={contact.emailHref} />
          <ContactMethod icon={icons.pin} label="Address" value={contact.address} />
        </VStack>

        <Box bg="white" p={{ base: 6, md: 8 }} borderRadius="xl" border="1px solid" borderColor="ink.100">
          <Heading as="h3" fontSize="lg" mb={5}>
            Send us a message
          </Heading>
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight={600} color="ink.600">
                  Name
                </FormLabel>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  autoComplete="name"
                  bg="white"
                  borderColor="ink.200"
                  _hover={{ borderColor: "ink.300" }}
                  focusBorderColor="brand.500"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight={600} color="ink.600">
                  Email
                </FormLabel>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@company.com"
                  autoComplete="email"
                  bg="white"
                  borderColor="ink.200"
                  _hover={{ borderColor: "ink.300" }}
                  focusBorderColor="brand.500"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight={600} color="ink.600">
                  Message
                </FormLabel>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us which products or services you're interested in."
                  rows={5}
                  bg="white"
                  borderColor="ink.200"
                  _hover={{ borderColor: "ink.300" }}
                  focusBorderColor="brand.500"
                />
              </FormControl>

              <Button
                type="submit"
                variant="brand"
                size="lg"
                width="full"
                isLoading={isSending}
                loadingText="Sending"
              >
                Send Message
              </Button>
            </VStack>
          </form>
        </Box>
      </SimpleGrid>
    </Section>
  );
}
